import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { getConsentStatus, isUsercentricsLoaded, addConsentChangedListener } from '../utils/usercentrics';
import { initializeVisitorCookie, clearVisitorCookie } from '../services/cookieService';

// Create context with default values - simplified to focus on chat cookie management
export const ConsentContext = createContext({
  functionalConsent: false,
  consentLoaded: false,
  openPreferences: () => {},
});

/**
 * Provider component for consent management
 * Focused only on functional consent for chat cookies
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.siteName - Site identifier used for cookie naming
 * @param {string} props.backendBaseUrl - Base URL for backend API endpoints
 * @param {boolean} [props.debug=false] - Enable debug logging
 */
export const ConsentProvider = ({ 
  children, 
  siteName, 
  backendBaseUrl,
  debug = false 
}) => {
  // Consent states - focused only on functional consent for chat cookies
  const [functionalConsent, setFunctionalConsent] = useState(false);
  const [consentLoaded, setConsentLoaded] = useState(false);

  // Track previous functional consent state to detect changes
  const prevFunctionalConsent = useRef(false);

  // Function to check current consent status
  const checkConsentStatus = useCallback(() => {
    if (!isUsercentricsLoaded()) {
      debug && console.log('[Consent] Usercentrics not loaded yet');
      return null;
    }
    
    const status = getConsentStatus();
    debug && console.log('[Consent] Current consent status:', status);
    return status;
  }, [debug]);

  // Handle cookie lifecycle based on consent status
  const handleCookieLifecycle = useCallback(async (hasConsent) => {
    if (!siteName || !backendBaseUrl) {
      // debug && console.error('[Consent] Missing configuration - siteName and backendBaseUrl required');
      return;
    }
    try {
      // const timestamp = new Date().toISOString();
      // debug && console.log(`[Consent DEBUG][${timestamp}] handleCookieLifecycle called with hasConsent:`, hasConsent);
      // debug && console.log(`[Consent DEBUG][${timestamp}] Prev consent was:`, prevFunctionalConsent.current);
      
      // Force API call regardless of previous state - always honor the current consent state
      // This ensures we're not relying on cached or stale consent state
      if (hasConsent) {
        // debug && console.log(`[Consent DEBUG][${timestamp}] INITIALIZING cookie - consent GRANTED`);
        // const startTime = performance.now();
        
        try {
          const result = await initializeVisitorCookie({
            siteName,
            backendBaseUrl,
            hasConsent: true, // Explicitly pass true to ensure cookie initialization
            debug
          });
          
          // const endTime = performance.now();
          // debug && console.log(`[Consent DEBUG][${timestamp}] Initialize cookie API call took ${(endTime - startTime).toFixed(2)}ms`);
          // debug && console.log(`[Consent DEBUG][${timestamp}] Initialize cookie result:`, result);
          
          // Check if API call was successful
          if (!result.success) {
            // debug && console.error(`[Consent DEBUG][${timestamp}] Cookie initialization API call FAILED:`, result.error || 'Unknown error');
          } else {
            // debug && console.log(`[Consent DEBUG][${timestamp}] Cookie initialization API call SUCCEEDED`);
          }
        } catch (initError) {
          // console.error(`[Consent DEBUG][${timestamp}] Exception during cookie initialization:`, initError);
        }
        
        // Check cookie presence after a delay to verify it was actually set
        setTimeout(() => {
          // debug && console.log(`[Consent DEBUG][${timestamp}] Checking cookies 500ms after init:`, document.cookie);
          // debug && console.log(`[Consent DEBUG][${timestamp}] Note: HTTP-only cookies won't appear here, check Network tab`);
        }, 500);
      } else {
        // debug && console.log(`[Consent DEBUG][${timestamp}] CLEARING cookie - consent DENIED/REVOKED`);
        // const startTime = performance.now();
        
        try {
          const result = await clearVisitorCookie({
            siteName,
            backendBaseUrl,
            debug
          });
          
          // const endTime = performance.now();
          // debug && console.log(`[Consent DEBUG][${timestamp}] Clear cookie API call took ${(endTime - startTime).toFixed(2)}ms`);
          // debug && console.log(`[Consent DEBUG][${timestamp}] Clear cookie result:`, result);
          
          // Check if API call was successful
          if (!result.success) {
            // console.error(`[Consent DEBUG][${timestamp}] Cookie clearing API call FAILED:`, result.error || 'Unknown error');
          } else {
            // debug && console.log(`[Consent DEBUG][${timestamp}] Cookie clearing API call SUCCEEDED`);
          }
        } catch (clearError) {
          // console.error(`[Consent DEBUG][${timestamp}] Exception during cookie clearing:`, clearError);
        }
        
        // Check cookie absence after a delay to verify it was actually cleared
        setTimeout(() => {
          // debug && console.log(`[Consent DEBUG][${timestamp}] Checking cookies 500ms after clear:`, document.cookie);
          // debug && console.log(`[Consent DEBUG][${timestamp}] Note: HTTP-only cookies won't appear here, check Network tab`);
        }, 500);
      }
      
      // Update previous consent ref to track changes
      prevFunctionalConsent.current = hasConsent;
    } catch (error) {
      // console.error('[Consent DEBUG] Error in handleCookieLifecycle:', error);
    }
  }, [siteName, backendBaseUrl, debug]);

  // Set up Usercentrics event listener and handle initial consent
  useEffect(() => {
    if (!siteName || !backendBaseUrl) {
      console.error('[Consent] Missing required config: siteName and backendBaseUrl');
      return;
    }

    // Keep reference to listener cleanup function
    let removeListener = null;
    
    // debug && console.log('[Consent DEBUG] Effect running with config:', { 
    //   siteName, 
    //   backendBaseUrl, 
    //   debug,
    //   consentLoaded: consentLoaded
    // });

    // Check if Usercentrics is loaded and set up listener
    const setupConsentListener = () => {
      const ucLoaded = isUsercentricsLoaded();
      
      if (ucLoaded) {
        // debug && console.log('[Consent DEBUG] Usercentrics loaded, setting up consent listener');
        
        // Get initial consent status
        const status = checkConsentStatus();
        // debug && console.log('[Consent DEBUG] Initial consent status:', status);
        
        if (status) {
          const hasFunctionalConsent = status.functional || false;
          // debug && console.log('[Consent DEBUG] Initial functional consent:', hasFunctionalConsent);
          
          // Set initial state
          setFunctionalConsent(hasFunctionalConsent);
          setConsentLoaded(true);
          
          // Handle initial cookie state
          // debug && console.log('[Consent DEBUG] Initializing cookies based on initial consent');
          handleCookieLifecycle(hasFunctionalConsent);
          prevFunctionalConsent.current = hasFunctionalConsent;
        } else {
          console.warn('[Consent DEBUG] Got null or undefined consent status');
        }
        
        // Add real-time consent change listener
        // debug && console.log('[Consent DEBUG] Setting up real-time consent change listener');
        removeListener = addConsentChangedListener((newStatus) => {
          // debug && console.log('[Consent DEBUG] *** REAL-TIME CONSENT CHANGE DETECTED ***');
          // debug && console.log('[Consent DEBUG] New consent status:', newStatus);
          
          const newFunctionalConsent = newStatus.functional || false;
          // debug && console.log('[Consent DEBUG] New functional consent:', newFunctionalConsent, 
          //                     'Previous:', prevFunctionalConsent.current);
          
          // Update React state
          setFunctionalConsent(newFunctionalConsent);
          
          // Ensure consentLoaded is set
          if (!consentLoaded) {
            // debug && console.log('[Consent DEBUG] Setting consentLoaded to true');
            setConsentLoaded(true);
          }
          
          // Always handle cookie lifecycle on consent change - don't rely on prevFunctionalConsent
          // This ensures we always try to initialize or clear cookies when consent changes
          // debug && console.log('[Consent DEBUG] Handling cookie lifecycle due to consent change');
          handleCookieLifecycle(newFunctionalConsent);
          
          // Update the previous consent value after handling the change
          prevFunctionalConsent.current = newFunctionalConsent;
        });
      } else {
        // Check again after a short delay
        // debug && console.log('[Consent DEBUG] Usercentrics not loaded yet, retrying in 500ms...');
        setTimeout(setupConsentListener, 500);
      }
    };
    
    // Start checking for Usercentrics
    setupConsentListener();
    
    // We're not using auto-consent anymore as testing happens on dev environment
    // We'll rely on the actual Usercentrics consent status in all environments
    // This ensures the same behavior in dev and production
    
    // Clean up listener on component unmount
    return () => {
      if (removeListener) removeListener();
    };
  }, [siteName, backendBaseUrl, debug, checkConsentStatus, handleCookieLifecycle, consentLoaded]);

  // Open Usercentrics preferences dialog
  const openPreferences = useCallback(() => {
    if (window.UC_UI && window.UC_UI.showSecondLayer) {
      window.UC_UI.showSecondLayer();
    } else if (window.usercentrics && window.usercentrics.showSecondLayer) {
      window.usercentrics.showSecondLayer();
    } else {
      console.warn('[Consent] Could not open preferences - Usercentrics not loaded');
    }
  }, []);
  
  // Context value to expose to components - simplified
  const contextValue = {
    functionalConsent,
    consentLoaded,
    openPreferences
  };
  
  return (
    <ConsentContext.Provider value={contextValue}>
      {children}
    </ConsentContext.Provider>
  );
};